import { z } from "zod";

import type { PermissionString } from "../permissions/presets";
import { InvitationState, MembershipStatus } from "./enums";

/**
 * Engine-agnostic identity contract. Every starter pack maps its auth/db engine
 * (Better Auth here, anything tomorrow) into these shapes so endpoints, features,
 * and the frontend stay identical regardless of what powers them underneath.
 */

export const domainUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DomainUser = z.infer<typeof domainUserSchema>;

export const domainOrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  logoUrl: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DomainOrganization = z.infer<typeof domainOrganizationSchema>;

export const roleSummarySchema = z.object({
  slug: z.string(),
  name: z.string(),
});
export type RoleSummary = z.infer<typeof roleSummarySchema>;

export const domainMembershipSchema = z.object({
  id: z.string(),
  userId: z.string(),
  organizationId: z.string(),
  organizationName: z.string(),
  role: roleSummarySchema.nullable(),
  status: z.enum(MembershipStatus),
  logoUrl: z.string().nullable(),
});
export type DomainMembership = z.infer<typeof domainMembershipSchema>;

const permissionStringSchema = z.custom<PermissionString>((value) => typeof value === "string");

export const userMeResponseSchema = z.object({
  user: domainUserSchema,
  organizationId: z.string().nullable(),
  organizations: z.array(domainMembershipSchema),
  role: roleSummarySchema.nullable(),
  permissions: z.array(permissionStringSchema).nullable(),
  avatarUrl: z.string().nullable(),
});
export type UserMeResponse = z.infer<typeof userMeResponseSchema>;

export const invitationRecordSchema = z.object({
  id: z.string(),
  email: z.string(),
  organizationId: z.string(),
  inviterId: z.string(),
  token: z.string(),
  state: z.enum(InvitationState),
  acceptedAt: z.date().nullable(),
  revokedAt: z.date().nullable(),
  expiresAt: z.date(),
  createdAt: z.date(),
});
export type InvitationRecord = z.infer<typeof invitationRecordSchema>;
